import { useEffect, useState } from 'react';
import { api } from '@services/api';

export type CustomFieldType = 'text' | 'number' | 'date' | 'select' | 'boolean';

export interface CustomFieldDefinition {
  id: string;
  entity_type: string;
  base_role: string | null;
  field_number: number;
  field_label: string;
  field_type: CustomFieldType;
  field_options: string[] | null;
  is_required: boolean;
  created_at: string;
  updated_at: string;
}

export type CustomFieldEntityType =
  | 'curriculum'
  | 'class'
  | 'course'
  | 'student'
  | 'user';

type CacheKey = string;
type CacheEntry = { definitions: CustomFieldDefinition[]; fetchedAt: number };

const CACHE: Map<CacheKey, CacheEntry> = new Map();
const INFLIGHT: Map<CacheKey, Promise<CustomFieldDefinition[]>> = new Map();
const TTL_MS = 5 * 60 * 1000;

function cacheKey(entityType: CustomFieldEntityType, baseRole?: string): CacheKey {
  return `${entityType}::${baseRole ?? ''}`;
}

async function fetchDefinitions(
  entityType: CustomFieldEntityType,
  baseRole?: string,
): Promise<CustomFieldDefinition[]> {
  let url = `/api/custom-field-definitions?entityType=${entityType}`;
  if (entityType === 'user' && baseRole) {
    url += `&baseRole=${baseRole}`;
  }
  const res = await api.get(url);
  const data = Array.isArray(res.data) ? res.data : (res.data?.definitions ?? []);
  return (data as CustomFieldDefinition[]).sort(
    (a, b) => a.field_number - b.field_number,
  );
}

export function invalidateCustomFieldDefinitions(
  entityType?: CustomFieldEntityType,
  baseRole?: string,
) {
  if (!entityType) {
    CACHE.clear();
    return;
  }
  CACHE.delete(cacheKey(entityType, baseRole));
}

export function useCustomFieldDefinitions(
  entityType: CustomFieldEntityType,
  baseRole?: string,
) {
  const key = cacheKey(entityType, baseRole);
  const cached = CACHE.get(key);
  const fresh = cached && Date.now() - cached.fetchedAt < TTL_MS;

  const [definitions, setDefinitions] = useState<CustomFieldDefinition[] | null>(
    fresh ? cached!.definitions : null,
  );
  const [loading, setLoading] = useState<boolean>(!fresh);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const existing = CACHE.get(key);
    if (existing && Date.now() - existing.fetchedAt < TTL_MS) {
      setDefinitions(existing.definitions);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let inflight = INFLIGHT.get(key);
    if (!inflight) {
      inflight = fetchDefinitions(entityType, baseRole)
        .then((defs) => {
          CACHE.set(key, { definitions: defs, fetchedAt: Date.now() });
          return defs;
        })
        .finally(() => {
          INFLIGHT.delete(key);
        });
      INFLIGHT.set(key, inflight);
    }

    inflight
      .then((defs) => {
        if (!cancelled) {
          setDefinitions(defs);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load');
          setDefinitions([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [key, entityType, baseRole]);

  return { definitions, loading, error };
}
